import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import { formatToKstFromUtc } from '@/shared/lib/format';
import { formatMetricValue } from '@/shared/lib/utils';

import { type DataZoneLowerCaseString, QUALITY_STATUS_COLORS } from '@/entities/quality';

import { DataZoneTableCell } from '@/features/quality/ui/dashboard/DataZoneTableCell';
import type { QualityStatusItem } from '@/features/quality/ui/dashboard/QualityStatusIndicator';
import { QualityStatusIndicator } from '@/features/quality/ui/dashboard/QualityStatusIndicator';
import { TableNameLink } from '@/features/quality/ui/dashboard/TableNameLink';

import type { ColumnQualityTableData } from './transformColumnQualityData';

const getColumnQualityStatusItems = (qualityStatus: ColumnQualityTableData['qualityStatus']): QualityStatusItem[] => [
  {
    icon: <CheckCircleOutlined style={{ color: QUALITY_STATUS_COLORS.GOOD }} />,
    count: qualityStatus.goodRows,
    ratio: qualityStatus.goodRatio,
  },
  {
    icon: <CloseCircleOutlined style={{ color: QUALITY_STATUS_COLORS.POOR }} />,
    count: qualityStatus.poorRows,
    ratio: qualityStatus.poorRatio,
  },
];

export const getColumnQualityColumns = (): ColumnsType<ColumnQualityTableData> => {
  return [
    {
      title: '테이블명',
      dataIndex: 'tableName',
      key: 'tableName',
      width: 200,
      fixed: 'left',
      ellipsis: true,
      render: (tableName: string, record: ColumnQualityTableData) => (
        <TableNameLink tableName={tableName} fqn={record.fqn} />
      ),
    },
    {
      title: '컬럼명',
      dataIndex: 'columnName',
      key: 'columnName',
      width: 200,
      fixed: 'left',
      ellipsis: true,
    },
    {
      title: '데이터 존',
      dataIndex: 'dataZone',
      key: 'dataZone',
      width: 150,
      render: (dataZone: DataZoneLowerCaseString) => {
        return <DataZoneTableCell dataZone={dataZone} />;
      },
    },
    {
      title: '전체 Row 수',
      dataIndex: 'cumulativeRowCount',
      key: 'cumulativeRowCount',
      width: 150,
      render: (count: number) => formatMetricValue(count),
    },
    {
      title: '신규 Row 수',
      dataIndex: 'incrementalRows',
      key: 'incrementalRows',
      width: 150,
      render: (count: number) => formatMetricValue(count),
    },
    {
      title: '평가 기준',
      dataIndex: 'testType',
      key: 'testType',
      width: 120,
      ellipsis: true,
    },
    {
      title: '신규 Row 품질 상태',
      dataIndex: 'qualityStatus',
      key: 'qualityStatus',
      width: 200,
      render: (qualityStatus: ColumnQualityTableData['qualityStatus']) => {
        const items = getColumnQualityStatusItems(qualityStatus);
        return <QualityStatusIndicator items={items} />;
      },
    },
    {
      title: '품질 평가 기준일',
      dataIndex: 'qualityEvaluationDate',
      key: 'qualityEvaluationDate',
      width: 200,
      render: (date: string) => formatToKstFromUtc(date),
    },
  ];
};
